<script lang="ts" setup>
import type { HTMLAttributes } from 'vue'

interface Props {
  modelValue?: string
  placeholder: string
  wrapperClass?: HTMLAttributes['class']
  inputClass?: HTMLAttributes['class']
  delay?: number
  noClearOnEscape?: boolean
}

interface Emits {
  (event: 'update:modelValue', value: string): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'reset'): void
}
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const isFocused = ref(false)

const query = ref(props.modelValue || '')
const fn = useDebounceFn((queryValue: string) => {
  emits('update:modelValue', queryValue)
}, props.delay ?? 200)

watch(query, async (value) => {
  fn(value)
})

const handleReset = () => {
  query.value = ''
  emits('update:modelValue', '')
  emits('reset')
}

const inputRef = ref<HTMLInputElement | null>(null)
useEventListener('keydown', (event) => {
  if (event.key.toLowerCase() === 'escape' && isFocused.value) {
    inputRef.value?.blur()
    if (props.noClearOnEscape)
      return
    handleReset()
  }
}, { passive: true })

const onFocus = () => {
  isFocused.value = true
  emits('focus')
}

const onBlur = () => {
  isFocused.value = false
  emits('blur')
}

defineExpose({
  isFocused,
  focus: () => {
    inputRef.value?.focus()
  },
  blur: () => {
    inputRef.value?.blur()
  },
  reset: handleReset,
})
</script>

<template>
  <div
    class="flex w-full gap-1" :class="[wrapperClass]"
  >
    <slot name="left-addon" :rest="handleReset" :is-empty="query.length === 0 ">
      <div class="flex items-center px-2">
        <IconSearch w-3 h-3 />
      </div>
    </slot>

    <input
      ref="inputRef"
      v-model="query"
      class="min-w-0 flex-1 bg-transparent text-softBlack placeholder-darkGray focus:outline-none text-sm leading-[1rem] placeholder-text-sm placeholder-leading-[1rem]" :class="[inputClass]"
      :placeholder="placeholder"
      @focus="onFocus()"
      @blur="onBlur()"
    >
    <slot name="right-addon" :rest="handleReset" :is-empty="query.length === 0 ">
      <button
        v-if="query.length"
        class="flex items-center px-2 hover:text-soft-black"
        aria-label="reset"
        @click="handleReset"
      >
        <div i-material-symbols:close />
      </button>
    </slot>
  </div>
</template>
